
import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Rocket, TestTube, MessageSquare, Zap, Check } from 'lucide-react';
import PageHero from '@/components/ui/page-hero';
import PageSection from '@/components/ui/page-section';
import SectionHeader from '@/components/ui/section-header';
import { useToast } from "@/components/ui/use-toast";

const BetaSignup: React.FC = () => {
  const { toast } = useToast();
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const betaProducts = [
    {
      id: "voice-chatbot",
      icon: <MessageSquare className="h-10 w-10 text-white" />,
      title: "AI Voice Chatbot",
      description: "Intelligent voice assistant with GoHighLevel integration for automated customer interactions",
      features: ["Natural language processing", "Voice recognition", "CRM integration", "Automated follow-ups"]
    },
    {
      id: "custom-personas",
      icon: <TestTube className="h-10 w-10 text-white" />,
      title: "AI Custom Personas & Avatars",
      description: "Interactive AI-driven customer experiences with personalized digital avatars",
      features: ["Customizable personalities", "Brand voice matching", "Multi-channel deployment", "Analytics dashboard"]
    }
  ];

  const betaBenefits = [
    {
      title: "Early Access",
      description: "Be among the first to use cutting-edge AI tools before they're publicly available"
    },
    {
      title: "Special Pricing",
      description: "Lock in exclusive beta pricing up to 40% below future public rates"
    },
    {
      title: "Priority Support",
      description: "Get direct access to our development team for personalized assistance"
    },
    {
      title: "Shape The Product",
      description: "Your feedback directly influences product development and features"
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, you'd send this data to your backend
    toast({
      title: "Application Submitted",
      description: "Thank you for applying to our beta program! We'll review your application and contact you soon.",
    });
    setFormSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <PageHero 
        badge="Limited Spots"
        title="Join Our AI Early Access Program"
        description="Gain exclusive access to our cutting-edge AI products while they're still in development. Help shape the future of AI and benefit from special beta pricing."
      />
      
      {/* How the Beta Works */}
      <PageSection>
        <SectionHeader
          title="How Our Beta Program Works"
          description="Get early access to innovative AI solutions and help us refine them before public release"
          centered={true}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <Card className="p-8 text-center border border-gray-200 shadow-md">
            <div className="mx-auto mb-6 p-4 rounded-full bg-blue-100 inline-flex items-center justify-center w-16 h-16">
              <Rocket className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold mb-4">Apply</h3>
            <p className="text-gray-700">
              Submit your application with details about your business and which AI solutions you're interested in testing.
            </p>
          </Card>
          
          <Card className="p-8 text-center border border-gray-200 shadow-md">
            <div className="mx-auto mb-6 p-4 rounded-full bg-blue-100 inline-flex items-center justify-center w-16 h-16">
              <Zap className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold mb-4">Onboard</h3>
            <p className="text-gray-700">
              Selected participants receive access credentials and personalized onboarding from our team.
            </p>
          </Card>
          
          <Card className="p-8 text-center border border-gray-200 shadow-md">
            <div className="mx-auto mb-6 p-4 rounded-full bg-blue-100 inline-flex items-center justify-center w-16 h-16">
              <MessageSquare className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold mb-4">Feedback</h3>
            <p className="text-gray-700">
              Use the products in your business and provide feedback to help us improve and refine the technology.
            </p>
          </Card>
        </div>
      </PageSection>
      
      {/* Beta Products */}
      <PageSection className="bg-gray-900 text-white">
        <SectionHeader
          title="Available Beta Products"
          description="Cutting-edge AI solutions currently in our beta program"
          centered={true}
          descriptionClassName="text-white/80"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {betaProducts.map((product, index) => (
            <Card 
              key={index} 
              className="border border-gray-700 bg-gray-800/60 glass-effect card-hover overflow-hidden"
            >
              <div className="p-8">
                <div className="mb-6 p-4 rounded-full bg-indigo-500/20 inline-block">
                  {product.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">
                  {product.title}
                </h3>
                <p className="text-white/80 mb-6">
                  {product.description}
                </p>
                
                <h4 className="font-semibold mb-3 text-white">Features:</h4>
                <ul className="mb-6 space-y-2 text-white/80">
                  {product.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start">
                      <span className="mr-2 text-indigo-400">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          ))}
        </div>
      </PageSection>
      
      {/* Beta Benefits */}
      <PageSection>
        <SectionHeader
          title="Benefits for Beta Testers"
          description="Join our beta program and enjoy these exclusive advantages"
          centered={true}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {betaBenefits.map((benefit, index) => (
            <div key={index} className="flex items-start">
              <div className="mr-4 mt-1 flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-700">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Application Form */}
      <PageSection className="bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <SectionHeader
            title="Apply for Beta Access"
            description="Complete the form below to apply for our beta program. Limited spots available."
            centered={true}
          />
          
          {formSubmitted ? (
            <Card className="p-8 text-center border border-green-200 bg-green-50">
              <div className="mx-auto mb-6 p-4 rounded-full bg-green-100 inline-flex items-center justify-center w-16 h-16">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-green-800">Application Submitted</h3>
              <p className="text-gray-700 mb-8">
                Thank you for your interest in our beta program! We'll review your application and get back to you within 2 business days.
              </p>
              <Button variant="outline" onClick={() => setFormSubmitted(false)}>
                Submit Another Application
              </Button>
            </Card>
          ) : (
            <Card className="p-8 border border-gray-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium">Full Name</label>
                    <Input id="name" placeholder="John Doe" required />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium">Email Address</label>
                    <Input id="email" type="email" placeholder="john@company.com" required />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="company" className="text-sm font-medium">Company Name</label>
                    <Input id="company" placeholder="Acme Inc." required />
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="role" className="text-sm font-medium">Your Role</label>
                    <Input id="role" placeholder="Marketing Director" required />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="product" className="text-sm font-medium">Interested In</label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="voice-chatbot">AI Voice Chatbot</SelectItem>
                      <SelectItem value="custom-personas">AI Custom Personas & Avatars</SelectItem>
                      <SelectItem value="both">Both Products</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="use-case" className="text-sm font-medium">How do you plan to use our AI products?</label>
                  <Textarea id="use-case" placeholder="Please describe your intended use case..." rows={4} />
                </div>
                
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                  Submit Application
                </Button>
              </form>
            </Card>
          )}
        </div>
      </PageSection>
      
      <Footer />
    </div>
  );
};

export default BetaSignup;
